import type {ScrapperResponse} from "@/model/Scrapper";
import type {AiBriefQuestion, AiCitedSource, AiWritingBrief, GapTopicSeed} from "@/model/AiWritingBrief";
import type {Article, ArticleContentKey, Keyword} from "@/model/Article";
import type {GeoTemplateId} from "@/config/WritingBrief";
import {normalizeGeoTemplate} from "@/config/WritingBrief";
import {humanizeAiAnswerMarkdown, insertPhrasesIntoHtml, normalizeBriefPhrase, textContainsPhrase} from "@/service/aiBriefCopy";
import {isJunkDensityKeyword} from "@/service/articleMainKeyword";

export {humanizeAiAnswerMarkdown, insertPhrasesIntoHtml, liveAnswerBody, liveAnswerNeedsExpand, normalizeBriefPhrase, textContainsPhrase} from "@/service/aiBriefCopy";
export {
    questionKeywordKey,
    questionToKeywordRow,
    mergeQuestionKeywordRows,
    mergeLsiVariantRows,
    looksLikeQuestionPhrase,
    partitionQuestionAndEntityPhrases,
    phraseToKeywordRow,
} from "@/service/questionKeywordRows";

function unique(values: Array<string | null | undefined>, max: number): string[] {
    const seen = new Set<string>();
    const result: string[] = [];
    for (const raw of values) {
        const text = String(raw || "").replace(/\s+/g, " ").trim();
        if (text.length < 2) {
            continue;
        }
        const key = text.toLowerCase();
        if (seen.has(key)) {
            continue;
        }
        seen.add(key);
        result.push(text);
        if (result.length >= max) {
            break;
        }
    }
    return result;
}

export function emptyAiWritingBrief(): AiWritingBrief {
    return {
        fetchedAt: Date.now(),
        questions: [],
        relatedSearches: [],
        entities: [],
        mustCoverHeadings: [],
        gapHeadings: [],
        answers: [],
        citationSnippets: [],
        extraSectionSeeds: [],
        organic: [],
        peopleAlsoSearch: [],
        autocomplete: [],
        citedSources: [],
        gapTopics: [],
        selectedCitationUrls: [],
        selectedFacts: [],
        introDirectives: [],
        keepAnswerShape: false,
        workedInPlatforms: [],
    };
}

export function mergeAiWritingBriefs(base: AiWritingBrief | null | undefined, next: AiWritingBrief | null | undefined): AiWritingBrief {
    if (!base) {
        return next || emptyAiWritingBrief();
    }
    if (!next) {
        return base;
    }
    const questions = new Map<string, AiBriefQuestion>();
    [...(base.questions || []), ...(next.questions || [])].forEach((item) => {
        const key = item.question.toLowerCase();
        const prev = questions.get(key);
        if (!prev || (item.aiSearchVolume || 0) > (prev.aiSearchVolume || 0) || (item.answerHint && !prev.answerHint)) {
            questions.set(key, {...prev, ...item});
        }
    });
    const answers = [...(next.answers || [])];
    for (const item of base.answers || []) {
        if (!answers.some((row) => row.platform === item.platform)) {
            answers.push(item);
        }
    }
    // Only the stage being merged in can vouch for the brief: a key left on the base
    // belongs to an earlier keyword, market or url (#30945).
    const fullStageKey = next.fullStageKey;
    return {
        fetchedAt: next.fetchedAt || base.fetchedAt,
        intent: next.intent || base.intent,
        suggestedGeoTemplate: next.suggestedGeoTemplate || base.suggestedGeoTemplate,
        snippetShape: next.snippetShape || base.snippetShape,
        questions: [...questions.values()].sort((a, b) => (b.aiSearchVolume || 0) - (a.aiSearchVolume || 0)).slice(0, 24),
        relatedSearches: unique([...(base.relatedSearches || []), ...(next.relatedSearches || [])], 10),
        entities: unique([...(base.entities || []), ...(next.entities || [])], 10),
        mustCoverHeadings: unique([...(next.mustCoverHeadings || []), ...(base.mustCoverHeadings || [])], 8),
        gapHeadings: unique([...(next.gapHeadings || []), ...(base.gapHeadings || [])], 8),
        answers,
        citationSnippets: unique([...(next.citationSnippets || []), ...(base.citationSnippets || [])], 8),
        extraSectionSeeds: unique([...(next.extraSectionSeeds || []), ...(base.extraSectionSeeds || [])], 10),
        organic: (next.organic && next.organic.length) ? next.organic : (base.organic || []),
        peopleAlsoSearch: unique([...(next.peopleAlsoSearch || []), ...(base.peopleAlsoSearch || [])], 10),
        autocomplete: unique([...(next.autocomplete || []), ...(base.autocomplete || [])], 10),
        citedSources: mergeCitedSources(base.citedSources, next.citedSources),
        gapTopics: mergeGapTopicRows(base.gapTopics, next.gapTopics),
        selectedCitationUrls: unique([...(base.selectedCitationUrls || []), ...(next.selectedCitationUrls || [])], 12),
        selectedFacts: unique([...(base.selectedFacts || []), ...(next.selectedFacts || [])], 12),
        introDirectives: unique([...(base.introDirectives || []), ...(next.introDirectives || [])], 8),
        keepAnswerShape: Boolean(next.keepAnswerShape || base.keepAnswerShape),
        workedInPlatforms: unique([...(base.workedInPlatforms || []), ...(next.workedInPlatforms || [])], 8),
        ...(fullStageKey ? {fullStageKey} : {}),
    };
}

/**
 * Identifies the keyword, market, language and article url a full brief was fetched for. The
 * full stage buys the AI answers, so an article that already has it saved does not fetch it
 * again (#30945). The url is part of it because ranked keywords and page intersection (gap
 * topics) are fetched for the article url; a new url fetches them again, while the AI answers
 * come from the cache, whose keys have no url.
 */
export function aiBriefFullStageKey(request: {
    keyword?: string | null;
    location_code?: string | number | null;
    language_code?: string | null;
    url?: string | null;
}): string {
    return [request.keyword, request.location_code, request.language_code, request.url]
        .map((value) => String(value ?? '').trim())
        .join('|');
}

/** The brief without its full-stage marker, e.g. once the main keyword it was fetched for changes. */
export function withoutFullStageKey(brief: AiWritingBrief): AiWritingBrief {
    const rest = {...brief};
    delete rest.fullStageKey;
    return rest;
}

export function hasFullAiBrief(
    brief: AiWritingBrief | null | undefined,
    request: Parameters<typeof aiBriefFullStageKey>[0]
): boolean {
    return Boolean(brief?.fullStageKey) && brief?.fullStageKey === aiBriefFullStageKey(request);
}

/**
 * The fast stage with the AI answers the saved brief already paid for (ChatGPT, Gemini,
 * Perplexity…; the Google AI overview comes with the fast stage) and their cited sources.
 * Nothing else is carried over: gap topics and seeds depend on the article url (#30945).
 */
export function keepPaidAiAnswers(saved: AiWritingBrief | null | undefined, fast: AiWritingBrief): AiWritingBrief {
    const fastPlatforms = new Set((fast.answers || []).map((answer) => answer.platform));
    const kept = (saved?.answers || []).filter((answer) => answer.platform !== 'googleAio' && !fastPlatforms.has(answer.platform));
    if (!kept.length) {
        return fast;
    }
    const keptPlatforms = new Set(kept.map((answer) => answer.platform));
    return {
        ...fast,
        answers: [...(fast.answers || []), ...kept],
        citedSources: mergeCitedSources(
            (saved?.citedSources || []).filter((source) => keptPlatforms.has(source.platform)),
            fast.citedSources
        ),
    };
}

export const MAX_SELECTED_CITATIONS = 12;

type BriefSelections = Pick<AiWritingBrief, 'selectedCitationUrls' | 'selectedFacts' | 'introDirectives' | 'keepAnswerShape' | 'workedInPlatforms'>;

export function briefSelections(brief: AiWritingBrief | null | undefined): BriefSelections {
    return {
        selectedCitationUrls: [...(brief?.selectedCitationUrls || [])],
        selectedFacts: [...(brief?.selectedFacts || [])],
        introDirectives: [...(brief?.introDirectives || [])],
        keepAnswerShape: Boolean(brief?.keepAnswerShape),
        workedInPlatforms: [...(brief?.workedInPlatforms || [])],
    };
}

/**
 * A refetched brief from the server never carries the author's picks
 * (cited sources, facts, kept shape…), so they must come from the saved brief.
 */
export function keepBriefSelections(saved: AiWritingBrief | null | undefined, fetched: AiWritingBrief): AiWritingBrief {
    return {...fetched, ...briefSelections(saved)};
}

export function toggleCitationUrl(selected: string[] | null | undefined, url: string): string[] {
    const current = [...(selected || [])];
    if (!url) {
        return current;
    }
    const index = current.indexOf(url);
    if (index === -1) {
        current.push(url);
    } else {
        current.splice(index, 1);
    }
    return current.slice(0, MAX_SELECTED_CITATIONS);
}

function mergeCitedSources(
    base: AiCitedSource[] | undefined,
    next: AiCitedSource[] | undefined
): AiCitedSource[] {
    const seen = new Set<string>();
    const result: AiCitedSource[] = [];
    for (const item of [...(next || []), ...(base || [])]) {
        const key = String(item?.url || "").toLowerCase();
        if (!key || seen.has(key)) {
            continue;
        }
        seen.add(key);
        result.push(item);
        if (result.length >= 12) {
            break;
        }
    }
    return result;
}

function mergeGapTopicRows(
    base: GapTopicSeed[] | undefined,
    next: GapTopicSeed[] | undefined
): GapTopicSeed[] {
    const map = new Map<string, GapTopicSeed>();
    for (const item of [...(base || []), ...(next || [])]) {
        const key = String(item?.keyword || "").toLowerCase();
        if (!key) {
            continue;
        }
        const prev = map.get(key);
        if (!prev || (item.searchVolume || 0) > (prev.searchVolume || 0)) {
            map.set(key, item);
        }
    }
    return [...map.values()]
        .sort((a, b) => (b.searchVolume || 0) - (a.searchVolume || 0))
        .slice(0, 10);
}

export function applyScrapedCoverage(brief: AiWritingBrief | null | undefined, scrapes: ScrapperResponse[], currentHeadings: string[] = []): AiWritingBrief {
    const merged = brief ? {...brief} : emptyAiWritingBrief();
    const counts = new Map<string, {title: string; count: number}>();
    for (const scrape of scrapes) {
        const local = new Set<string>();
        for (const heading of [...(scrape.h2h || []), ...(scrape.h3h || [])]) {
            const title = String(heading || "").replace(/\s+/g, " ").trim();
            if (title.length < 3) {
                continue;
            }
            const key = title.toLowerCase();
            if (local.has(key)) {
                continue;
            }
            local.add(key);
            const prev = counts.get(key);
            if (prev) {
                prev.count += 1;
            } else {
                counts.set(key, {title, count: 1});
            }
        }
    }
    const shared = [...counts.values()]
        .filter((item) => item.count >= 2)
        .sort((a, b) => b.count - a.count)
        .slice(0, 8)
        .map((item) => item.title)
        .filter((title) => !isJunkDensityKeyword(title));
    const current = new Set(currentHeadings.map((item) => item.toLowerCase()));
    merged.mustCoverHeadings = unique([...shared, ...(merged.mustCoverHeadings || [])], 8);
    merged.gapHeadings = unique([
        ...shared.filter((title) => !current.has(title.toLowerCase())),
        ...(merged.gapHeadings || []),
        ...(merged.extraSectionSeeds || []),
    ], 8);
    return merged;
}

export function shouldAutoApplyTemplate(
    current: GeoTemplateId | undefined,
    suggested?: GeoTemplateId,
    userPicked?: boolean
): suggested is GeoTemplateId {
    const next = suggested ? normalizeGeoTemplate(suggested) : undefined;
    const cur = normalizeGeoTemplate(current);
    return !!next && !userPicked && cur === "universal" && next !== "universal";
}

export type BriefFact = {
    id: string;
    sentence: string;
    platform?: string;
};

export type ArticleTextTarget = {
    contentPart: ArticleContentKey;
    index: number;
    tag: "h" | "p";
};

function asSentence(text: string): string {
    const value = String(text || "").replace(/\s+/g, " ").trim();
    if (!value) {
        return "";
    }
    return /[.!?…]$/.test(value) ? value : value + ".";
}

function isConcreteFact(text: string): boolean {
    return /\d/.test(text) || /[%€$£]|procent|percent|mld\.?\b|tisíc|tis\./i.test(text);
}

export function extractFactsFromBrief(brief: AiWritingBrief | null | undefined): BriefFact[] {
    if (!brief) {
        return [];
    }
    const facts: BriefFact[] = [];
    const seen = new Set<string>();
    const add = (raw: string, platform?: string) => {
        const sentence = asSentence(humanizeAiAnswerMarkdown(raw));
        if (!sentence || !isConcreteFact(sentence)) {
            return;
        }
        if (sentence.split(/\s+/).length > 42) {
            return;
        }
        const key = normalizeBriefPhrase(sentence);
        if (!key || seen.has(key)) {
            return;
        }
        seen.add(key);
        facts.push({id: key.slice(0, 96), sentence, platform});
    };
    for (const answer of brief.answers || []) {
        for (const snippet of answer.citedSnippets || []) {
            add(snippet, answer.platform);
        }
        const readable = humanizeAiAnswerMarkdown(answer.markdown || "");
        for (const part of readable.split(/(?<=[.!?])\s+|\n+/)) {
            add(part, answer.platform);
        }
    }
    for (const entity of brief.entities || []) {
        if (isConcreteFact(entity)) {
            add(entity);
        }
    }
    for (const snippet of brief.citationSnippets || []) {
        add(snippet);
    }
    return facts.slice(0, 12);
}

export function firstBodyParagraph(article: Article): ArticleTextTarget {
    if (article.h1 && article.h1.length) {
        return {contentPart: "h1", index: 0, tag: "p"};
    }
    if (article.h2 && article.h2.length) {
        return {contentPart: "h2", index: 0, tag: "p"};
    }
    return {contentPart: "h1", index: 0, tag: "p"};
}

export function findPhraseInArticle(article: Article, phrase: string): ArticleTextTarget | null {
    const needle = normalizeBriefPhrase(phrase);
    if (!needle || needle.length < 4) {
        return null;
    }
    const distinctive = (phrase.match(/\d+(?:[.,]\d+)?\s*%?/g) || []).map((item) => item.replace(/\s+/g, ""));
    const matches = (haystack: string): boolean => {
        const text = normalizeBriefPhrase(haystack);
        if (!text) {
            return false;
        }
        if (textContainsPhrase(haystack, phrase) || (needle.length >= 12 && text.includes(needle.slice(0, Math.min(48, needle.length))))) {
            return true;
        }
        return distinctive.some((token) => token.length >= 2 && text.replace(/\s+/g, "").includes(token.toLowerCase()));
    };
    for (let level = 1; level <= 6; level++) {
        const part = ("h" + level) as ArticleContentKey;
        const blocks = article[part] || [];
        for (let index = 0; index < blocks.length; index++) {
            if (matches(blocks[index].p)) {
                return {contentPart: part, index, tag: "p"};
            }
            if (matches(blocks[index].h)) {
                return {contentPart: part, index, tag: "h"};
            }
        }
    }
    return null;
}

export function editorAnchor(target: ArticleTextTarget): string {
    return "#" + target.contentPart + target.tag + "w" + (target.index ? String(target.index) : "");
}

export function rememberBriefValue(brief: AiWritingBrief, field: "selectedFacts" | "introDirectives", value: string): AiWritingBrief {
    const current = [...(brief[field] || [])];
    const key = value.toLowerCase();
    if (!current.some((item) => item.toLowerCase() === key)) {
        current.push(value);
    }
    return {
        ...brief,
        [field]: current.slice(0, field === "selectedFacts" ? 12 : 8),
    };
}

export function markLsiOnDensityList(article: Article, phrases: string[]): void {
    const secondary = Array.isArray(article.secondaryKeywords) ? [...article.secondaryKeywords] : [];
    const all = Array.isArray(article.allKeywords) ? [...article.allKeywords] : [];
    for (const raw of phrases) {
        const phrase = String(raw || "").replace(/\s+/g, " ").trim();
        const key = normalizeBriefPhrase(phrase);
        if (!phrase || !key || isJunkDensityKeyword(phrase, article.selectedKeyword || article.keyword)) {
            continue;
        }
        if (!secondary.some((item) => normalizeBriefPhrase(item) === key)) {
            secondary.push(phrase);
        }
        const existing = all.find((item) => normalizeBriefPhrase(item.keyword) === key);
        if (existing) {
            existing.checked = true;
            if (!existing.source || existing.source === "custom") {
                existing.source = "secondary";
            }
        } else {
            all.push({keyword: phrase, checked: true, source: "secondary"} as Keyword);
        }
    }
    article.secondaryKeywords = secondary;
    article.allKeywords = all;
}

export function articleHasPhrase(article: Article | null | undefined, phrase: string): boolean {
    return !!article && !!findPhraseInArticle(article, phrase);
}


export function factKeywordHint(sentence: string): string {
    const match = sentence.match(/.{0,28}\d+(?:[.,]\d+)?\s*%?.{0,28}/);
    return (match ? match[0] : sentence).replace(/\s+/g, " ").trim().slice(0, 80);
}

export function findHeadingInArticle(article: Article, heading: string): ArticleTextTarget | null {
    const needle = normalizeBriefPhrase(heading);
    if (!needle) {
        return null;
    }
    for (let level = 2; level <= 6; level++) {
        const part = ("h" + level) as ArticleContentKey;
        const blocks = article[part] || [];
        for (let index = 0; index < blocks.length; index++) {
            const text = normalizeBriefPhrase(blocks[index].h);
            if (text === needle || (needle.length >= 12 && (text.includes(needle) || needle.includes(text)))) {
                return {contentPart: part, index, tag: "h"};
            }
        }
    }
    return null;
}

export function insertPhraseIntoHtml(html: string, phrase: string): string {
    return insertPhrasesIntoHtml(html, [phrase], "fact", "cs").html;
}
